import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import type {
  NcDashboard, NcFinding, NcFindingDetail, NcFindingQuery, NcFindingActionRequest, NcScanResult,
  NcAssignee, NcNotification, NcRuleInfo, NcResidentDetail, NcResidentMds, NcMdsAssessmentSave,
  NcMdsReviewRequest, NcConsistencyRow, NcConsistencyReviewRequest, PdpmAlertConfig, NcCorporateSummary,
  NcWeightDataReview, NcWeightReviewRequest, NcSignoffItem, NcSignoffRequest,
} from '../types/pdpm';

// Nutrition Compliance (route /pdpm). Every read is one evaluation of Dietech's records on the server, so the
// dashboard, worklist, resident view and corporate roll-up never disagree about a resident.

const pdpmKeys = {
  all: ['pdpm'] as const,
  dashboard: (facilityId: number) => [...pdpmKeys.all, 'dashboard', facilityId] as const,
  findings: (facilityId: number, query?: NcFindingQuery) => [...pdpmKeys.all, 'findings', facilityId, query ?? {}] as const,
  finding: (facilityId: number, id: number) => [...pdpmKeys.all, 'finding', facilityId, id] as const,
  assignees: (facilityId: number) => [...pdpmKeys.all, 'assignees', facilityId] as const,
  rules: (facilityId: number) => [...pdpmKeys.all, 'rules', facilityId] as const,
  resident: (facilityId: number, patientId: number) => [...pdpmKeys.all, 'resident', facilityId, patientId] as const,
  mds: (facilityId: number, patientId: number) => [...pdpmKeys.all, 'mds', facilityId, patientId] as const,
  consistencies: (facilityId: number) => [...pdpmKeys.all, 'consistencies', facilityId] as const,
  weightData: (facilityId: number, months: number) => [...pdpmKeys.all, 'weightData', facilityId, months] as const,
  signoffs: (facilityId: number) => [...pdpmKeys.all, 'signoffs', facilityId] as const,
  alertConfig: (facilityId: number) => [...pdpmKeys.all, 'alertConfig', facilityId] as const,
  corporate: (corporateFacilityId: number) => [...pdpmKeys.all, 'corporate', corporateFacilityId] as const,
};

/** Query string for a findings query (only the values set). */
export function findingsParams(query: NcFindingQuery): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '' && v !== false) params[k] = String(v);
  }
  return params;
}

export function useNcDashboard(facilityId: number) {
  return useQuery({
    queryKey: pdpmKeys.dashboard(facilityId),
    queryFn: async (): Promise<NcDashboard> => (await apiClient.get<NcDashboard>(API.pdpmDashboard(facilityId))).data,
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useNcFindings(facilityId: number, query: NcFindingQuery) {
  return useQuery({
    queryKey: pdpmKeys.findings(facilityId, query),
    queryFn: async (): Promise<NcFinding[]> =>
      (await apiClient.get<NcFinding[]>(API.pdpmFindings(facilityId), { params: findingsParams(query) })).data ?? [],
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

export function useNcFinding(facilityId: number, id: number | null) {
  return useQuery({
    queryKey: pdpmKeys.finding(facilityId, id ?? 0),
    queryFn: async (): Promise<NcFindingDetail> => (await apiClient.get<NcFindingDetail>(API.pdpmFinding(facilityId, id!))).data,
    enabled: facilityId > 0 && (id ?? 0) > 0,
  });
}

export function useNcAssignees(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.assignees(facilityId),
    queryFn: async (): Promise<NcAssignee[]> => (await apiClient.get<NcAssignee[]>(API.pdpmAssignees(facilityId))).data ?? [],
    enabled: enabled && facilityId > 0,
    staleTime: 10 * 60 * 1000,
  });
}

export function useNcRules(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.rules(facilityId),
    queryFn: async (): Promise<NcRuleInfo[]> => (await apiClient.get<NcRuleInfo[]>(API.pdpmRules(facilityId))).data ?? [],
    enabled: enabled && facilityId > 0,
  });
}

/** Every rule and method with this facility's clinical sign-off status and record. */
export function useNcSignoffs(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.signoffs(facilityId),
    queryFn: async (): Promise<NcSignoffItem[]> => (await apiClient.get<NcSignoffItem[]>(API.pdpmSignoffs(facilityId))).data ?? [],
    enabled: enabled && facilityId > 0,
  });
}

export function useNcRecordSignoff(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: NcSignoffRequest) => {
      await apiClient.post(API.pdpmSignoffs(facilityId), request);
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcResident(facilityId: number, patientId: number | null) {
  return useQuery({
    queryKey: pdpmKeys.resident(facilityId, patientId ?? 0),
    queryFn: async (): Promise<NcResidentDetail> => (await apiClient.get<NcResidentDetail>(API.pdpmResident(facilityId, patientId!))).data,
    enabled: facilityId > 0 && (patientId ?? 0) > 0,
  });
}

export function useNcResidentMds(facilityId: number, patientId: number | null, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.mds(facilityId, patientId ?? 0),
    queryFn: async (): Promise<NcResidentMds> => (await apiClient.get<NcResidentMds>(API.pdpmMds(facilityId, patientId!))).data,
    enabled: enabled && facilityId > 0 && (patientId ?? 0) > 0,
  });
}

export function useNcConsistencies(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.consistencies(facilityId),
    queryFn: async (): Promise<NcConsistencyRow[]> => (await apiClient.get<NcConsistencyRow[]>(API.pdpmConsistencies(facilityId))).data ?? [],
    enabled: enabled && facilityId > 0,
  });
}

/** Weight entries that look wrong over the last `months` months (active residents). Read-only. */
export function useNcWeightData(facilityId: number, months: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.weightData(facilityId, months),
    queryFn: async (): Promise<NcWeightDataReview> => (await apiClient.get<NcWeightDataReview>(API.pdpmWeightData(facilityId, months))).data,
    enabled: enabled && facilityId > 0,
  });
}

export type NcReportFormat = 'pdf' | 'xlsx';

/**
 * Fetches a report the API builds (PDF or Excel) and saves it as a file. Reports hold resident information, so the
 * API also requires the Reports level.
 */
export async function downloadNcReport(url: string, params: Record<string, string>, fileName: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(url, { params, responseType: 'blob' });
  const link = document.createElement('a');
  const href = URL.createObjectURL(data);
  link.href = href;
  link.download = `${fileName}.${params.format === 'xlsx' ? 'xlsx' : 'pdf'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

/** The bell's feed: no resident names, keyed so the same event is never announced twice. */
export async function fetchNcNotifications(facilityId: number): Promise<NcNotification[]> {
  const { data } = await apiClient.get<NcNotification[]>(API.pdpmNotifications(facilityId), { _skipSessionExpiry: true } as never);
  return data ?? [];
}

/** Brings the stored findings up to date. Evaluating twice changes nothing; the page runs it on open and on Refresh. */
export function useNcEvaluate(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<NcScanResult> => (await apiClient.post<NcScanResult>(API.pdpmEvaluate(facilityId))).data,
    // The page shows a failed evaluation itself; this keeps the app-wide "Operation failed" toast away.
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcFindingAction(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: NcFindingActionRequest) => {
      await apiClient.post(API.pdpmFindingAction(facilityId), request);
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcSaveMds(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (save: NcMdsAssessmentSave): Promise<number> => (await apiClient.post<number>(API.pdpmMdsSave(facilityId), save)).data,
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcDeleteMds(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.pdpmMdsDelete(facilityId, id));
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcReviewMds(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: NcMdsReviewRequest) => {
      await apiClient.post(API.pdpmMdsReview(facilityId, patientId), request);
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function useNcReviewConsistency(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: NcConsistencyReviewRequest) => {
      await apiClient.post(API.pdpmConsistencyReview(facilityId), request);
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

/** A reviewer's decision about one weight. The weight itself is not changed; corrections are made in weight entry. */
export function useNcReviewWeight(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: NcWeightReviewRequest) => {
      await apiClient.post(API.pdpmWeightReview(facilityId), request);
    },
    onError: () => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

export function usePdpmAlertConfig(facilityId: number) {
  return useQuery({
    queryKey: pdpmKeys.alertConfig(facilityId),
    queryFn: async (): Promise<PdpmAlertConfig> => (await apiClient.get<PdpmAlertConfig>(API.pdpmAlertConfig(facilityId))).data,
    enabled: facilityId > 0,
  });
}

export function useSavePdpmAlertConfig(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config: PdpmAlertConfig): Promise<number> =>
      (await apiClient.post<number>(API.pdpmSaveAlertConfig(facilityId), config)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pdpmKeys.all }),
  });
}

/**
 * The roll-up at a corporate facility (every facility the user may see under it).
 * `enabled` waits for the facility settings to say the facility is corporate.
 */
export function usePdpmCorporateSummary(corporateFacilityId: number, enabled = true) {
  return useQuery({
    queryKey: pdpmKeys.corporate(corporateFacilityId),
    queryFn: async (): Promise<NcCorporateSummary> =>
      (await apiClient.get<NcCorporateSummary>(API.pdpmCorporateSummary(corporateFacilityId))).data,
    enabled: enabled && corporateFacilityId > 0,
    staleTime: 5 * 60 * 1000,
    // A 403 (no Reports access, or not the user's corporate facility) will not change on a retry.
    retry: false,
  });
}

export { pdpmKeys };
